// src/lib/api.ts

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

// ⚙️ INTELLIGENTE KONFIGURATION
const getBaseUrl = (): string => {
  if (typeof window !== "undefined" && window.APP_CONFIG?.VITE_BACKEND_URL) {
    return window.APP_CONFIG.VITE_BACKEND_URL;
  }
  if (import.meta.env.VITE_BACKEND_URL) {
    return import.meta.env.VITE_BACKEND_URL;
  }
  return "https://api.callevo.io";
};

const BASE_URL = getBaseUrl().replace(/\/$/, "");

let accessTokenCache: string | null = null;

/** Called by AuthContext on every auth state change (login, logout, token refresh). */
export function setApiAccessToken(token: string | null): void {
  accessTokenCache = token;
}

function getAccessToken(): string | null {
  return accessTokenCache;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Authorization: token ? `Bearer ${token}` : "",
    ...options.headers,
  };

  if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const config = { ...options, headers };

  const url = endpoint.startsWith("http")
    ? endpoint
    : `${BASE_URL}${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;

  const response = await fetch(url, config);

  // Rate Limit (429) und Unauthorized (401)
  if (response.status === 429) {
    throw new Error("Zu viele Anfragen! Bitte warte einen Moment (Rate Limit).");
  }
  if (response.status === 401) {
    throw new Error("SESSION_EXPIRED");
  }

  if (!response.ok) {
    let errorDetails = response.statusText;
    try {
      const errJson = await response.json();
      errorDetails = errJson.error || errJson.message || JSON.stringify(errJson);
    } catch (e) {
      // Ignorieren falls kein JSON Body
    }
    throw new Error(errorDetails || `API Error (${response.status})`);
  }

  return response.json() as Promise<T>;
}

// Expliziter Export des api Objekts
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body: BodyInit | object, options: RequestOptions = {}) => {
    const finalBody = body instanceof FormData ? body : JSON.stringify(body);
    return request<T>(endpoint, { ...options, method: "POST", body: finalBody });
  },

  put: <T>(endpoint: string, body: BodyInit | object, options: RequestOptions = {}) => {
    const finalBody = body instanceof FormData ? body : JSON.stringify(body);
    return request<T>(endpoint, { ...options, method: "PUT", body: finalBody });
  },

  del: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),
};
